/* eslint-disable jsx-a11y/tabindex-no-positive */
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { ReactElement, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useStoreState } from '~/store/hooks';
import formatSheltersOptions from '~/utils/formatSheltersOptions';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import Checkbox from '~/ui/components/inputs/Checkbox';
import Input from '~/ui/components/inputs/Input';
import InputMask from '~/ui/components/inputs/InputMask';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import useDictionaryOptions from '~/ui/pages/Clients/LocalAdmin/Form/hooks/useDictionaryOptions';
import { IInitialValues } from '~/ui/pages/Clients/LocalAdmin/Form/initialValues';

import styles from './Location.module.scss';

const Location = (): ReactElement => {
  const {
    control,
    register,
    watch,
    unregister,
    formState: { errors },
  } = useFormContext<IInitialValues>();

  const availableShelters = useStoreState(state => state.shelters.availableShelters);

  const shelterOptions = formatSheltersOptions(availableShelters);
  const stateOptions = useDictionaryOptions(IDictionaryTypes.State);

  const isShelter = watch('isShelter');

  useEffect(() => {
    if (isShelter) {
      unregister(['address']);
    } else {
      unregister(['shelterId']);
    }
  }, [isShelter, unregister]);

  return (
    <div className={styles.marginTop}>
      <Paper>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                sm: 12,
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <p className={styles.sectionTitle}>Location</p>
                </Grid>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Checkbox
                      size="small"
                      name="isShelter"
                      control={control}
                      errors={errors}
                      label="Shelter"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            {!isShelter ? (
              <>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Input
                    tabIndex={14}
                    register={register}
                    errors={errors}
                    name="address.city"
                    label="City"
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Input
                    tabIndex={18}
                    register={register}
                    errors={errors}
                    name="address.line1"
                    label="Address 1"
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 3,
                  }}
                >
                  <Select
                    tabIndex={15}
                    options={stateOptions}
                    name="address.stateId"
                    control={control}
                    errors={errors}
                    label="State"
                    hideSelectedOptions={false}
                    openMenuOnFocus
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 3,
                  }}
                >
                  <InputMask
                    tabIndex={16}
                    alwaysShowMask={false}
                    mask="99999"
                    name="address.zipCode"
                    control={control}
                    errors={errors}
                    label="Zip Code"
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Input
                    tabIndex={19}
                    register={register}
                    errors={errors}
                    name="address.line2"
                    label="Address 2"
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Input
                    tabIndex={17}
                    register={register}
                    errors={errors}
                    name="address.poBox"
                    label="PO Box"
                  />
                </Grid>
                <Grid
                  size={{
                    sm: 6,
                  }}
                >
                  <Input
                    tabIndex={20}
                    register={register}
                    errors={errors}
                    name="address.shelterName"
                    label="Name"
                  />
                </Grid>
              </>
            ) : (
              <Grid
                size={{
                  sm: 6,
                }}
              >
                <Select
                  tabIndex={14}
                  options={shelterOptions}
                  name="shelterId"
                  control={control}
                  errors={errors}
                  label="Select Shelter"
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>
    </div>
  );
};

export default Location;
